import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { designLineApi, type DesignLineContext } from '../api/design-line-service';
import type { EstimateSummary } from '../api/estimate-service';
import type { Album } from '@/features/design/album/api/album-service';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { ApiClientError } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('useDesignLine');

/** L4 watchdog cadence while a line's estimate or album is being updated. */
const UPDATING_POLL_MS = 5000;

/**
 * The design's estimate/album context (docs/estimate/UI-SPEC-design-lines.md).
 * Reconcile: L1 on mount and after every estimate/album mutation, L2/L3 via
 * the `estimates.all` sweep on WS reconnect / tab foreground, WS `estimates`
 * and `albums` events invalidate it, and while any line is updating the query
 * polls as the L4 terminal-state backstop.
 */
export function useDesignLineContext(projectId: string, conceptId: string) {
  return useQuery({
    queryKey: cacheKeys.estimates.designLineContext(conceptId),
    queryFn: () => designLineApi.context(projectId, conceptId),
    enabled: Boolean(projectId && conceptId),
    staleTime: 30 * 1000,
    refetchInterval: (query) => {
      const data = query.state.data as DesignLineContext | undefined;
      const isUpdating = data?.lines.some(
        (l) => l.is_updating || l.is_album_updating
      );
      return isUpdating ? UPDATING_POLL_MS : false;
    },
  });
}

/**
 * The estimate the design's Estimate tab shows: the current version of the
 * design's line (not the newest estimate of the design).
 */
export function useConceptCurrentEstimate(projectId: string, conceptId: string) {
  const query = useDesignLineContext(projectId, conceptId);
  return {
    context: query.data,
    estimate: query.data?.line?.currentEstimate ?? undefined,
    isLoading: query.isLoading,
  };
}

/** The building's lines: one estimate card / album card per line. */
export function useProjectDesignLines(projectId: string) {
  return useQuery({
    queryKey: cacheKeys.estimates.projectDesignLines(projectId),
    queryFn: () => designLineApi.listByProject(projectId),
    enabled: Boolean(projectId),
    staleTime: 30 * 1000,
  });
}

export type DesignLineHistoryKind = 'estimate' | 'album';

/** Version history of a line (estimate or album), newest first. */
export function useDesignLineVersions(
  kind: DesignLineHistoryKind,
  lineId: string | undefined,
  enabled: boolean
) {
  return useQuery<Array<EstimateSummary | Album>>({
    queryKey:
      kind === 'estimate'
        ? cacheKeys.estimates.lineEstimateVersions(lineId ?? '')
        : cacheKeys.estimates.lineAlbumVersions(lineId ?? ''),
    queryFn: () =>
      kind === 'estimate'
        ? designLineApi.estimateVersions(lineId as string)
        : designLineApi.albumVersions(lineId as string),
    enabled: Boolean(lineId) && enabled,
  });
}

/** "Restore this version": a new current version with that content. */
export function useRestoreDesignLineVersion(kind: DesignLineHistoryKind) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ lineId, versionId }: { lineId: string; versionId: string }) =>
      kind === 'estimate'
        ? designLineApi.restoreEstimate(lineId, versionId)
        : designLineApi.restoreAlbum(lineId, versionId),
    onSuccess: () => {
      // L1: the restored copy is the line's new head everywhere it shows.
      queryClient.invalidateQueries({ queryKey: cacheKeys.estimates.all });
      if (kind === 'album') {
        queryClient.invalidateQueries({ queryKey: cacheKeys.albums.all });
        queryClient.invalidateQueries({ queryKey: cacheKeys.projectAlbums.all });
      }
    },
    onError: (error: unknown) => {
      logger.error('Design line restore failed', { kind, error });
      toast.error(
        error instanceof ApiClientError
          ? error.getFirstError()
          : error instanceof Error
            ? error.message
            : 'Failed to restore version'
      );
    },
  });
}

/**
 * "Delete estimate" in the concept page kebab: deletes the design's whole line
 * (all estimate versions and its blueprints), not just the current version.
 * Other devices reconcile via WS `estimates/deleted` / `albums/deleted`.
 */
export function useDeleteDesignLine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lineId: string) => designLineApi.deleteLine(lineId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cacheKeys.estimates.all });
      queryClient.invalidateQueries({ queryKey: cacheKeys.albums.all });
      queryClient.invalidateQueries({ queryKey: cacheKeys.projectAlbums.all });
    },
    onError: (error: unknown) => {
      logger.error('Design line delete failed', { error });
      toast.error(
        error instanceof ApiClientError
          ? error.getFirstError()
          : error instanceof Error
            ? error.message
            : 'Failed to delete estimate'
      );
    },
  });
}
